/**
 * Componentes de presentacion puros para el asistente -- sin logica de
 * negocio, reconstruidos sobre el mismo patron visual que ya usa
 * ActivityDetail.tsx (bordes/colores via variables CSS del tema).
 */
import type { CSSProperties, InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react';

const inputStyle: CSSProperties = {
  padding: '8px 10px',
  border: '1px solid var(--border)',
  borderRadius: 6,
  background: 'var(--ground)',
  color: 'var(--ink)',
  font: 'inherit',
  width: '100%',
};

export function Field({
  label,
  hint,
  error,
  required,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <div style={{ display: 'grid', gap: 6, marginBottom: 14 }}>
      <label style={{ fontSize: 13, fontWeight: 600 }}>
        {label}
        {required && <span style={{ color: 'var(--critical)' }}> *</span>}
      </label>
      {hint && (
        <p className="muted" style={{ fontSize: 12, margin: 0 }}>
          {hint}
        </p>
      )}
      {children}
      {error && (
        <p style={{ color: 'var(--critical)', fontSize: 12, margin: 0 }}>
          {error}
        </p>
      )}
    </div>
  );
}

export function TextInput(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} style={{ ...inputStyle, ...props.style }} />;
}

export function TextArea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea rows={4} {...props} style={{ ...inputStyle, resize: 'vertical', ...props.style }} />;
}

export function SelectInput(props: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} style={{ ...inputStyle, ...props.style }} />;
}

export function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  );
}

/** Multi-select por checkboxes, con badge opcional (p. ej. "sensible"). */
export function CheckList({
  options,
  selected,
  onChange,
}: {
  options: Array<{ code: string; label: string; badge?: string }>;
  selected: string[];
  onChange: (codes: string[]) => void;
}) {
  const toggle = (code: string) => {
    onChange(selected.includes(code) ? selected.filter((c) => c !== code) : [...selected, code]);
  };
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      {options.map((o) => (
        <label
          key={o.code}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 13,
            padding: '6px 8px',
            border: '1px solid var(--border)',
            borderRadius: 6,
            cursor: 'pointer',
            background: selected.includes(o.code) ? 'var(--surface)' : 'transparent',
          }}
        >
          <input type="checkbox" checked={selected.includes(o.code)} onChange={() => toggle(o.code)} />
          <span style={{ flex: 1 }}>{o.label}</span>
          {o.badge && (
            <span className="pill" style={{ background: 'var(--critical-weak)', color: 'var(--critical)', fontSize: 11 }}>
              {o.badge}
            </span>
          )}
        </label>
      ))}
    </div>
  );
}
